import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import isError from 'lodash/isError';

import { StripeContext } from './StripeContext';

export interface StripeProviderProps {
  /** Stripe public key. */
  publishableKey: string;
  /** A callback to handle errors while loading the Stripe API. */
  onLoadError?: (error: Error) => void;
  children?: ReactNode | ReactNode[];
}

/** Initializes Stripe API and create a context to access the global instance. */
export function StripeProvider(props: StripeProviderProps) {
  const { onLoadError, publishableKey } = props;
  const [stripeLoadError, setStripeLoadError] = useState<Error>();
  const [stripeInstance, setStripeInstance] = useState<Stripe>();

  /** Load stripe API */
  useEffect(() => {
    if (stripeInstance) {
      // Stripe is initialized, nothing to do.
      return;
    }
    if (stripeLoadError) {
      // Could not load stripe
      return;
    }
    async function load() {
      try {
        const instance = await loadStripe(publishableKey, {
          apiVersion: '2023-10-16',
        });
        setStripeInstance(instance ? instance : undefined);
      } catch (err) {
        if (!isError(err)) {
          return;
        }
        setStripeLoadError(err);
        onLoadError?.(err);
      }
    }
    void load();
  }, [stripeLoadError, stripeInstance, publishableKey, onLoadError]);

  const stripeContextValue = useMemo(
    () => ({
      stripe: stripeInstance,
      stripeLoadError: stripeLoadError,
    }),
    [stripeInstance, stripeLoadError]
  );

  return (
    <StripeContext.Provider value={stripeContextValue}>
      {props.children}
    </StripeContext.Provider>
  );
}
